import { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { useAdAccount } from "../lib/adAccount";
import {
  PaperPlaneRight, Plus, Trash, Wrench, Sparkle, Lightning,
  ChartLineUp, MagnifyingGlass, Pause, Play, CurrencyCircleDollar
} from "@phosphor-icons/react";

const STARTER_PROMPTS = [
  { icon: ChartLineUp, label: "Daily report 7 hari lepas", prompt: "Bagi aku daily report 7 hari lepas — total spend, subscribes, cost per subscribe, top creatives, dan apa yang patut aku buat untuk improve." },
  { icon: MagnifyingGlass, label: "Audit semua creatives", prompt: "Audit semua creatives — yang mana patut scale, yang mana patut pause, dan yang mana fatigued perlu refresh. Focus on cost per subscribe." },
  { icon: Pause, label: "Pause underperformers", prompt: "Cari creative atau campaign dengan cost per subscribe tinggi (>RM30) atau subscribes rendah. Suggest mana yang patut pause." },
  { icon: CurrencyCircleDollar, label: "Scale top subscribers", prompt: "Top 3 creative by subscribe volume dengan cost per subscribe paling rendah — patut aku naikkan budget berapa untuk scale?" },
];

const TOOL_LABELS = {
  list_campaigns: "Listing campaigns",
  create_campaign: "Creating campaign",
  pause_campaign: "Pausing campaign",
  resume_campaign: "Resuming campaign",
  update_campaign_budget: "Updating budget",
  create_ad_set: "Creating ad set",
  create_ad: "Creating ad",
  get_account_insights: "Fetching account insights",
  get_campaign_insights: "Fetching campaign insights",
  get_top_campaigns: "Ranking campaigns",
  get_ads_with_verdict: "Auditing creatives",
  get_audience_breakdown: "Analyzing audience",
};

function ToolCallCard({ tc }) {
  const ok = !tc.result?.status || tc.result?.status === "success";
  return (
    <div data-testid="tool-call-card" className="bg-[#0A0A0A] border border-orange-500/30 px-3 py-2 my-1.5 font-mono text-[11px] flex items-start gap-2 max-w-[85%]">
      <Wrench size={12} className="text-orange-500 mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-orange-400 font-semibold">{TOOL_LABELS[tc.name] || tc.name}</span>
          <span className={`px-1.5 py-px text-[9px] uppercase tracking-wider ${ok ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
            {ok ? "OK" : "ERR"}
          </span>
        </div>
        {Object.keys(tc.args || {}).length > 0 && (
          <div className="text-zinc-500 text-[10px] mt-0.5 truncate">
            {JSON.stringify(tc.args)}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageBubble({ msg }) {
  if (msg.role === "user") {
    return (
      <div className="flex justify-end mb-4" data-testid="message-user">
        <div className="bg-zinc-800/80 text-zinc-50 px-3.5 py-2 max-w-[80%] text-sm leading-relaxed">
          {msg.content}
        </div>
      </div>
    );
  }
  return (
    <div className="mb-5" data-testid="message-assistant">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 bg-orange-500 flex items-center justify-center">
          <Sparkle size={11} weight="fill" className="text-zinc-950" />
        </div>
        <span className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">Underads Metaverse</span>
      </div>
      {msg.tool_calls_log?.map((tc) => <ToolCallCard key={tc.id} tc={tc} />)}
      {msg.content && (
        <div className="prose-chat text-sm pl-7">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
        </div>
      )}
    </div>
  );
}

export default function Chat() {
  const { selected } = useAdAccount();
  const [conversations, setConversations] = useState([]);
  const [activeConvId, setActiveConvId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const refreshConversations = async () => {
    try {
      const r = await api.get("/conversations");
      setConversations(r.data || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { refreshConversations(); }, []);

  // Pickup pre-filled prompt from "Ask AI" button on Creatives page
  useEffect(() => {
    const pre = sessionStorage.getItem("prefilledPrompt");
    if (pre) {
      sessionStorage.removeItem("prefilledPrompt");
      setActiveConvId(null);
      setMessages([]);
      setTimeout(() => send(pre), 100);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!activeConvId) { setMessages([]); return; }
    api.get(`/conversations/${activeConvId}`).then(r => {
      setMessages(r.data.messages || []);
    });
  }, [activeConvId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    if (!text.trim() || sending || !selected) return;
    const userMsg = { id: "u-" + Date.now(), role: "user", content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post("/agent/chat", {
        conversation_id: activeConvId,
        ad_account_id: selected,
        message: text,
      });
      if (!activeConvId) setActiveConvId(r.data.conversation_id);
      setMessages((m) => [...m, r.data.message]);
      refreshConversations();
    } catch (e) {
      setMessages((m) => [...m, {
        id: "e-" + Date.now(), role: "assistant",
        content: "Error: " + (e.response?.data?.detail || e.message),
        tool_calls_log: [], created_at: new Date().toISOString(),
      }]);
    } finally { setSending(false); }
  };

  const newChat = () => { setActiveConvId(null); setMessages([]); };

  const deleteConv = async (id) => {
    await api.delete(`/conversations/${id}`);
    if (id === activeConvId) newChat();
    refreshConversations();
  };

  return (
    <div className="flex flex-1 overflow-hidden">
      {/* Conversations sidebar */}
      <div className="w-60 border-r border-zinc-900 bg-[#070707] flex flex-col">
        <div className="p-3 border-b border-zinc-900">
          <button
            data-testid="new-chat-button"
            onClick={newChat}
            className="w-full flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-zinc-950 text-sm font-semibold py-2 transition-colors"
          >
            <Plus size={14} weight="bold" /> New chat
          </button>
        </div>
        <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-600 px-3 pt-3 pb-1.5">History</div>
        <div className="flex-1 overflow-y-auto px-2 pb-2 space-y-0.5">
          {conversations.length === 0 && (
            <div className="text-xs text-zinc-600 px-2 py-3 italic">No conversations yet</div>
          )}
          {conversations.map((c) => (
            <div
              key={c.id}
              data-testid={`conversation-${c.id}`}
              className={`group flex items-center gap-1 px-2 py-1.5 cursor-pointer text-xs transition-colors ${
                c.id === activeConvId ? "bg-zinc-900 border-l-2 border-orange-500" : "hover:bg-zinc-900/50 border-l-2 border-transparent"
              }`}
              onClick={() => setActiveConvId(c.id)}
            >
              <div className="flex-1 truncate text-zinc-300">{c.title}</div>
              <button
                onClick={(e) => { e.stopPropagation(); deleteConv(c.id); }}
                className="opacity-0 group-hover:opacity-100 text-zinc-500 hover:text-red-400"
              >
                <Trash size={11} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Chat main */}
      <div className="flex-1 flex flex-col bg-[#050505]">
        {/* Header */}
        <div className="h-14 border-b border-zinc-900 px-6 flex items-center backdrop-blur-xl bg-zinc-950/70">
          <div>
            <div className="text-[10px] font-mono tracking-[0.2em] uppercase text-zinc-500">// Chat</div>
            <div className="text-sm font-medium">Underads Metaverse Agent</div>
          </div>
          <div className="ml-auto flex items-center gap-2 text-[10px] font-mono text-zinc-500">
            <Lightning size={11} weight="fill" className="text-orange-500" />
            CLAUDE OPUS 4.6 · 1M CTX
          </div>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6">
          {messages.length === 0 && !sending && (
            <div className="max-w-2xl mx-auto pt-12">
              <div className="text-xs font-mono tracking-[0.3em] text-orange-500 uppercase mb-3">// Ready</div>
              <h1 className="font-display text-4xl font-black tracking-tighter mb-3">
                Apa yang awak nak buat<br />dengan Meta Ads hari ni?
              </h1>
              <p className="text-zinc-500 text-sm mb-8 max-w-lg">
                Tanya apa-apa. Buat campaign, edit budget, analyse performance, atau dapatkan advisor recommendation. AI akan execute secara autonomous.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {STARTER_PROMPTS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`starter-prompt-${i}`}
                    onClick={() => send(s.prompt)}
                    className="flex items-start gap-3 bg-[#0A0A0A] border border-zinc-800 hover:border-orange-500/50 px-3.5 py-3 text-left transition-colors group"
                  >
                    <s.icon size={16} className="text-orange-500 mt-0.5 shrink-0" />
                    <div>
                      <div className="text-sm text-zinc-200 font-medium mb-0.5">{s.label}</div>
                      <div className="text-[11px] text-zinc-500 leading-relaxed">{s.prompt}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m) => <MessageBubble key={m.id} msg={m} />)}
          {sending && (
            <div data-testid="agent-thinking" className="flex items-center gap-2 mb-4 text-[11px] font-mono">
              <div className="w-5 h-5 bg-orange-500 flex items-center justify-center animate-pulse-orange">
                <Sparkle size={11} weight="fill" className="text-zinc-950" />
              </div>
              <span className="text-zinc-500 tracking-wider">AGENT IS THINKING...</span>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="border-t border-zinc-900 p-4">
          <form
            onSubmit={(e) => { e.preventDefault(); send(input); }}
            className="max-w-3xl mx-auto bg-[#0A0A0A] border border-zinc-800 focus-within:border-orange-500/60 transition-colors flex items-end"
          >
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send(input);
                }
              }}
              rows={1}
              placeholder="Tanya tentang ad performance, atau prompt untuk create / edit campaign..."
              className="flex-1 bg-transparent px-3.5 py-3 text-sm resize-none focus:outline-none placeholder-zinc-600 max-h-32"
              style={{ minHeight: "48px" }}
            />
            <button
              data-testid="chat-send-button"
              type="submit"
              disabled={sending || !input.trim()}
              className="m-2 w-8 h-8 bg-orange-500 hover:bg-orange-600 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-950 flex items-center justify-center transition-colors"
            >
              <PaperPlaneRight size={14} weight="fill" />
            </button>
          </form>
          <div className="max-w-3xl mx-auto mt-2 text-[10px] font-mono text-zinc-600 text-center">
            All actions execute on the selected ad account. Default new campaigns are PAUSED for safety.
          </div>
        </div>
      </div>
    </div>
  );
}
